/* Dr.Primal — the quiz funnel engine (sections/dp-quiz.liquid).

   One page, every screen pre-rendered and hidden; this file decides which one shows,
   the same division of labour as dp-capture.js: Liquid owns markup and copy, JS owns
   sequence and state. Nothing here formats money and nothing here fetches — the CRM
   and analytics calls go through the adapters dp-capture's files registered on
   `window.dpCapture.adapters`, so there is exactly one Klaviyo client in the theme.

   The rules that shape it:
   - The flow is built per segment: Q1's answer picks a stretch of segment questions
     woven around the universal q3/q4 (v4 spec). Inside a built flow only two screens
     are conditional: the hormonal catch `q8` (female only — why it sits after q12 in
     display order despite its doc name) and `q10` (skipped when q9 was "none" alone).
   - The visitor can go BACK freely and FORWARD only to where they have already been.
     The report stages additionally require `gated` (an address submitted, or the gate
     skipped) so a deep link like #plan on a fresh session lands on q1, not on the
     report. That is the hole the reference funnel shipped with, closed.
   - The email gate fails soft in one direction only: a malformed address blocks with
     a message, but a network or server failure does NOT hold the visitor's report
     hostage — it logs, tracks, and lets them through. The address is a lead, not a
     ransom.
   - State lives in localStorage under one key, restored on load, so a refresh resumes
     where they were. Storage can throw (private mode); every touch is wrapped.

   The pure decision functions live on `window.dpQuiz.core` so tests/dp-quiz/ can run
   this exact file under Node with the dp-capture harness. */
(function () {
  'use strict';

  /* The flow is BUILT per segment (v4 spec, 2026-09-15): a shared head (entry, router,
     info), then the Q1 path's own stretch in the order the spec fixes around the
     universal q3/q4, then the shared tail. q12, the gender question, IS the entry
     screen — the landing page's Male/Female pills are its answers — which is what lets
     the catch-all's hormonal catch (q8, the spec's C4) read a gender that is always
     already answered. The catch-all reuses q5/q6/q7/q8 as its C1 to C4, ids unchanged,
     so the segment table and the Klaviyo properties never moved. Before Q1 is
     answered the stretch is just q3/q4, which only matters to the step counter.
     One route moves a head screen: the removal stretch places q2, the info screen,
     after d2 ("were you told what to expect?") so it lands as the reply to that
     answer, and flowFor takes it out of the head for that route alone. The removal
     route (change guide, 2026-10-18) leaves out q3 and d3, puts urgency (d4) straight
     after the info screen that explains it, and asks d6 only after a yes to d4. */
  var PRE = ['q12', 'q1', 'q2'];
  var STRETCH = {
    weightloss: ['a1', 'a2', 'q3', 'q4', 'a3', 'a4', 'a5'],
    hormonal: ['b1', 'b2', 'q3', 'q4', 'b3', 'b4', 'b5'],
    postop: ['d1', 'd2', 'q2', 'd4', 'd6', 'q4', 'd5'],
    nafld: ['e1', 'e2', 'e3', 'q3', 'q4', 'e4', 'e5'],
    // The right-rib pressure route (2026-09-25): choosing it already answers q3, so the
    // stretch skips it and takes the catch-all's gallbladder questions.
    stagnant: ['q4', 'q5', 'c5', 'c6', 'c7'],
    digestion: ['q5', 'q3', 'q4', 'q6', 'q7', 'q8', 'c5', 'c6', 'c7'],
  };
  var DEFAULT_STRETCH = ['q3', 'q4'];
  var TAIL = ['q9', 'q10', 'q11', 'q16', 'gate', 'loader', 'summary', 'failed', 'mechanism', 'proof', 'plan'];
  var GATE = 'gate';
  var ENTRY = PRE[0];
  var STORAGE_KEY = 'dp-quiz:v1';
  var SOURCE = 'Dr.Primal quiz';
  var EVENT_NAME = 'Quiz Completed';            // the Klaviyo metric a flow can trigger on
  var EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var LOADER_ROW_MS = 1500;                     // the FIRST tick; each next lands at 60% of the last, ~3.5s over five rows
  var LOADER_DONE_MS = 1200;                    // pause after the last tick before the summary
  var REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
  var TRIED_MIN = 3;                            // q11's tried list never shows fewer rows

  /* ---- Pure decisions. Everything the tests exercise. --------------------------- */

  // The summary restates the visitor's own answers as flags; these are the flag
  // conditions from sections/dp-quiz.liquid's `.dp-quiz__flags` list, mirrored so the
  // step can be SKIPPED when none would show. Change a flag there, change it here.
  var FLAG_RULES = [
    ['q1', 'stagnant'],
    ['q3', 'yes'], ['q4', 'yes'], ['q5', 'yes'],
    ['a3', 'yes'], ['a4', 'yes'], ['a5', 'yes'],
    ['b5', 'yes'], ['b3', 'yes'],
    ['c5', 'yes'], ['c7', 'yes'],
    ['d2', 'no'], ['d3', 'no'], ['d4', 'yes'], ['d5', 'yes'], ['d6', 'yes'],
    ['e2', 'yes'],
  ];

  // Every screen the visitor's route can reach, before the conditionals below.
  function stepsFor(answers) {
    var stretch = STRETCH[answers.q1] || DEFAULT_STRETCH;
    var head = PRE.filter(function (id) {
      return stretch.indexOf(id) === -1;
    });
    return head.concat(stretch, TAIL);
  }

  // An answer counts only while its screen is on the visitor's current route. Answers
  // are never pruned, so one given on another route, or on a screen a later version
  // of the flow removed, stays in storage; it must not light up a flag or a copy
  // variant on this one.
  function answered(answers, id, value) {
    if (stepsFor(answers).indexOf(id) === -1) return false;
    var answer = answers[id];
    return Array.isArray(answer) ? answer.indexOf(value) !== -1 : answer === value;
  }

  // The conditionals inside a built flow: q8 (the catch-all's hormonal catch, female
  // only), d6 (only after a yes to d4), q10 (nothing to rate when q9 was "none"
  // alone), and the summary, skipped when no flag would light up.
  function eligible(id, answers) {
    if (id === 'q8') return answers.q12 === 'female';
    if (id === 'd6') return answers.d4 === 'yes';
    if (id === 'q10') {
      var tried = answers.q9 || [];
      return !(tried.length === 1 && tried[0] === 'none');
    }
    if (id === 'summary') {
      return FLAG_RULES.some(function (rule) {
        return answered(answers, rule[0], rule[1]);
      });
    }
    return true;
  }

  function flowFor(answers) {
    return stepsFor(answers).filter(function (id) {
      return eligible(id, answers);
    });
  }

  function nextStep(current, answers) {
    var flow = flowFor(answers);
    var index = flow.indexOf(current);
    if (index === -1 || index === flow.length - 1) return null;
    return flow[index + 1];
  }

  function previousStep(current, answers) {
    var flow = flowFor(answers);
    var index = flow.indexOf(current);
    if (index <= 0) return null;
    return flow[index - 1];
  }

  /* The routing table from the content doc. Q1 decides directly for five segments;
     the catch-all resolves by modifier priority B > G > F > C. */
  function computeSegment(answers) {
    switch (answers.q1) {
      case 'weightloss': return 'A';
      case 'hormonal': return 'B';
      case 'postop': return 'D';
      case 'nafld': return 'E';
      case 'stagnant': return 'H';
    }
    if (answers.q8 === 'yes') return 'B';
    if (answers.q7 === 'yes') return 'G';
    if (answers.q6 === 'yes') return 'F';
    return 'C';
  }

  /* A target is visitable when the visitor has already been there or further, and —
     past the gate — only once gated. The gate itself is pre-gate: reachable ungated. */
  function allowedTarget(id, state) {
    var flow = flowFor(state.answers);
    var target = flow.indexOf(id);
    if (target === -1) return false;
    var reached = flow.indexOf(state.reached);
    if (target > reached) return false;
    if (target > flow.indexOf(GATE)) return Boolean(state.gated);
    return true;
  }

  // The entry screen is outside the counted quiz: progress and the step count begin
  // at q1 and run to the gate.
  function isIntro(id, answers) {
    return flowFor(answers).indexOf(id) === 0;
  }

  function progressPercent(id, answers) {
    var flow = flowFor(answers);
    var gateIndex = flow.indexOf(GATE);
    var index = flow.indexOf(id);
    if (index <= 0) return 0;
    if (index === -1 || index > gateIndex) return 100;
    return Math.round(((index - 1) / (gateIndex - 1)) * 100);
  }

  // "3 / 16": the position among the counted screens the gate closes, gate included.
  // Null on the entry screen and past the gate, where the bar carries no count.
  function stepCount(id, answers) {
    var flow = flowFor(answers);
    var gateIndex = flow.indexOf(GATE);
    var index = flow.indexOf(id);
    if (index <= 0 || index > gateIndex) return null;
    return { position: index, total: gateIndex };
  }

  function isPostGate(id) {
    return TAIL.indexOf(id) > TAIL.indexOf(GATE);
  }

  /* ---- The page. ----------------------------------------------------------------- */

  var booted = false;

  function init() {
    if (booted) return;
    var root = document.querySelector('[data-dp-quiz]');
    if (!root) return;
    booted = true;

    var nojs = root.querySelector('[data-dp-quiz-nojs]');
    if (nojs) nojs.hidden = true;

    var bar = root.querySelector('[data-dp-quiz-bar]');
    var progressBar = root.querySelector('[data-dp-quiz-progressbar]');
    var progressFill = root.querySelector('[data-dp-quiz-progress]');
    var backButton = root.querySelector('[data-dp-quiz-back]');
    var countNode = root.querySelector('[data-dp-quiz-count]');
    var timerNode = root.querySelector('[data-dp-quiz-timer]');
    var timerRunning = false;

    /* The plan stage's offer countdown: 9:59, its deadline in sessionStorage so a
       refresh or a walk back through the report resumes the same clock rather than
       restarting it. It parks at 0:00; nothing else changes when it gets there. */
    function startTimer() {
      if (timerRunning || !timerNode) return;
      var count = timerNode.querySelector('[data-dp-quiz-timer-count]');
      if (!count) return;
      timerRunning = true;

      var limit = 599 * 1000;
      var deadline = 0;
      try {
        deadline = parseInt(window.sessionStorage.getItem('dp-quiz:deadline'), 10) || 0;
      } catch (error) {
        // Private mode: the clock still runs, it just restarts on the next load.
      }
      if (!deadline || deadline - Date.now() > limit) {
        deadline = Date.now() + limit;
        try {
          window.sessionStorage.setItem('dp-quiz:deadline', String(deadline));
        } catch (error) {
          // As above.
        }
      }

      function tick() {
        var left = Math.max(0, Math.round((deadline - Date.now()) / 1000));
        count.textContent = Math.floor(left / 60) + ':' + (left % 60 < 10 ? '0' : '') + (left % 60);
        if (left > 0) window.setTimeout(tick, 250);
      }
      tick();
    }
    var barRow = root.querySelector('.dp-quiz__bar-row');
    var stickTicking = false;

    function syncTimerStick() {
      stickTicking = false;
      if (!timerNode || !bar) return;
      var stuck = !timerNode.hidden && Boolean(barRow) && barRow.getBoundingClientRect().bottom <= 0;
      timerNode.classList.toggle('dp-quiz__timer--stuck', stuck);
      bar.classList.toggle('dp-quiz__bar--timer-stuck', stuck);
    }

    window.addEventListener('scroll', function () {
      if (stickTicking) return;
      stickTicking = true;
      window.requestAnimationFrame(syncTimerStick);
    }, { passive: true });

    var countCurrent = root.querySelector('[data-dp-quiz-count-current]');
    var countTotal = root.querySelector('[data-dp-quiz-count-total]');

    function stepRoot(id) {
      return root.querySelector('[data-dp-quiz-step="' + id + '"]');
    }

    function reducedMotion() {
      return Boolean(window.matchMedia && window.matchMedia(REDUCED_MOTION_QUERY).matches);
    }

    /* ---- state ---- */

    var state = { answers: {}, reached: ENTRY, gated: false, segment: '' };

    function load() {
      try {
        var raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return;
        var parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object' && parsed.answers) state = parsed;
      } catch (error) {
        // Private mode or corrupt JSON: a fresh run, not a failure.
      }
    }

    function save() {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      } catch (error) {
        // Nothing to do: the run still works, it just will not survive a refresh.
      }
    }

    /* ---- adapters, shared with the capture popup ---- */

    function adapters() {
      return (window.dpCapture && window.dpCapture.adapters) || {};
    }

    function track(event, props) {
      try {
        var analytics = adapters().analytics;
        if (!analytics) return;
        var base = { funnel: 'quiz-v3', segment: state.segment || '' };
        Object.keys(props || {}).forEach(function (key) {
          base[key] = props[key];
        });
        analytics.track(event, base);
      } catch (error) {
        console.error('[dp-quiz] track failed', error);
      }
    }

    function publish(event, params) {
      if (window.dpGa4) window.dpGa4.event(event, params);
    }

    /* ---- rendering ---- */

    function ruleHit(rule) {
      var parts = rule.split(':');
      return answered(state.answers, parts[0], parts[1]);
    }

    /* q11's "why it misses" list on the removal route: whatever the visitor ticked at
       q9 moves to the top, in the list's own order, carrying a "You tried this" tag.
       Three or more ticked shows only those; fewer is topped up from the rest, in the
       list's order, to three rows (client, 2026-10-18). */
    function sortTried(container) {
      var list = container.querySelector('[data-dp-quiz-tried-list]');
      if (!list) return;
      var items = Array.prototype.slice.call(list.querySelectorAll('[data-dp-q9]'));
      var ticked = [];
      var rest = [];
      items.forEach(function (item) {
        var hit = answered(state.answers, 'q9', item.getAttribute('data-dp-q9'));
        var tag = item.querySelector('[data-dp-quiz-tried-tag]');
        if (tag) tag.hidden = !hit;
        (hit ? ticked : rest).push(item);
      });
      var shown = Math.max(ticked.length, TRIED_MIN);
      ticked.concat(rest).forEach(function (item, index) {
        item.hidden = index >= shown;
        list.appendChild(item);
      });
    }

    function applyConditionals(container) {
      var nodes = container.querySelectorAll('[data-dp-quiz-if]');
      Array.prototype.forEach.call(nodes, function (node) {
        node.hidden = !ruleHit(node.getAttribute('data-dp-quiz-if'));
      });
      var inverses = container.querySelectorAll('[data-dp-quiz-unless]');
      Array.prototype.forEach.call(inverses, function (node) {
        node.hidden = ruleHit(node.getAttribute('data-dp-quiz-unless'));
      });
      var segments = container.querySelectorAll('[data-dp-quiz-seg]');
      Array.prototype.forEach.call(segments, function (node) {
        node.hidden = node.getAttribute('data-dp-quiz-seg') !== state.segment;
      });
      sortTried(container);
    }

    var current = null;

    function show(id, options) {
      var target = stepRoot(id);
      if (!target) return;

      Array.prototype.forEach.call(root.querySelectorAll('[data-dp-quiz-step]'), function (node) {
        node.hidden = true;
      });

      applyConditionals(target);
      target.hidden = false;
      current = id;

      var flow = flowFor(state.answers);
      if (flow.indexOf(id) > flow.indexOf(state.reached)) {
        state.reached = id;
        save();

        var reachedCount = stepCount(id, state.answers);
        if (reachedCount) {
          publish('quiz_step', {
            step_number: reachedCount.position,
            step_total: reachedCount.total,
            step_id: id,
          });
        }
        if (isPostGate(id) && id !== 'loader' && !state.completed) {
          state.completed = true;
          save();
          publish('quiz_complete', { email_given: Boolean(state.emailed) });
        }
        if (id === 'plan') publish('quiz_plan_view', {});
      }

      var percent = progressPercent(id, state.answers);
      if (progressFill) progressFill.style.width = percent + '%';
      if (progressBar) progressBar.setAttribute('aria-valuenow', String(percent));

      var count = stepCount(id, state.answers);
      if (countNode) countNode.hidden = !count;
      if (countCurrent) countCurrent.textContent = count ? String(count.position) : '';
      if (countTotal) countTotal.textContent = count ? '/ ' + count.total : '';

      // The report register: past the gate the progress and count disappear, as does
      // back — the report is read forward, and back into the gate would re-ask for the
      // email. The entry screen keeps the (empty) progress track so the bar's edge does
      // not jump in when the first answer lands; only the count stays off there.
      var post = isPostGate(id);
      if (progressBar) progressBar.hidden = post;
      if (timerNode) {
        timerNode.hidden = id !== 'plan';
        if (id === 'plan') startTimer();
      }
      syncTimerStick();
      if (backButton) backButton.hidden = post || previousStep(id, state.answers) === null;

      // Toggled, not added: leaving the plan stage (back, or a restored hash) must take
      // the picker section with it, or it trails under every other screen.
      var main = document.querySelector('.dp-main');
      if (main) main.classList.toggle('dp-quiz-plan', id === 'plan');

      if (!options || !options.silent) {
        try {
          window.history.pushState({ dpQuiz: id }, '', '#' + id);
        } catch (error) {
          // History can throw in sandboxed frames; the quiz works without it.
        }
      }

      // preventScroll, and focus BEFORE the scroll: focusing the step scrolls it into
      // view under the sticky bar, which dragged every tall step down a little after
      // scrollTo had already put it at the top.
      if (typeof target.focus === 'function') {
        try {
          target.focus({ preventScroll: true });
        } catch (error) {
          target.focus();
        }
      }
      window.scrollTo(0, 0);

      track('Quiz Screen', { screen: id });

      if (id === 'loader') runLoader();
    }

    function advance() {
      var next = nextStep(current, state.answers);
      if (next) show(next);
    }

    /* ---- the loader's staged ticks ---- */

    function runLoader() {
      var rows = stepRoot('loader').querySelectorAll('[data-dp-quiz-loader-row]');
      var instant = reducedMotion();

      // Every row is on screen from the first frame, spinning; they turn to checks one
      // by one, so the wait reads as work rather than as an empty screen. The ticks
      // ACCELERATE: the first row takes the full LOADER_ROW_MS and each one after
      // lands at 60% of the previous wait, so the analysis reads as gathering pace
      // instead of a metronome.
      var elapsed = 0;
      var interval = instant ? 0 : LOADER_ROW_MS;
      Array.prototype.forEach.call(rows, function (row) {
        row.removeAttribute('data-done');
        elapsed += interval;
        window.setTimeout(function () {
          row.setAttribute('data-done', 'true');
        }, elapsed);
        interval = Math.round(interval * 0.6);
      });

      window.setTimeout(function () {
        // Only move on if the visitor is still on the loader: back/refresh both cancel.
        // Through nextStep, not a hardcoded target: with no flags to show, the summary
        // is not in the flow and the loader lands on the next report stage.
        if (current === 'loader') advance();
      }, elapsed + (instant ? 300 : LOADER_DONE_MS));
    }

    /* ---- the gate ---- */

    function submitGate(form) {
      var emailInput = form.querySelector('[data-dp-quiz-email]');
      var honeypot = form.querySelector('[data-dp-quiz-honeypot]');
      var errorNode = form.querySelector('[data-dp-quiz-gate-error]') ||
        stepRoot(GATE).querySelector('[data-dp-quiz-gate-error]');
      var email = (emailInput && emailInput.value || '').trim();

      function fail(message) {
        if (!errorNode) return;
        errorNode.textContent = message;
        errorNode.hidden = false;
      }

      // A filled honeypot is a bot: pretend success, store nothing, send nothing.
      if (honeypot && honeypot.value) {
        state.gated = true;
        save();
        show('loader');
        return;
      }

      if (!EMAIL_SHAPE.test(email)) {
        fail('That email address does not look right. Check it and try again.');
        return;
      }

      if (errorNode) errorNode.hidden = true;

      state.segment = computeSegment(state.answers);
      save();

      function through() {
        state.gated = true;
        state.emailed = true;
        save();
        publish('generate_lead', { lead_source: 'quiz' });
        track('Quiz Submitted', {});
        show('loader');
      }

      var crm = adapters().crm;
      var config = window.dpCapture && window.dpCapture.config;
      if (!crm || !config) {
        console.error('[dp-quiz] no CRM adapter on this page; the report opens, the lead is lost');
        through();
        return;
      }

      var tried = state.answers.q9 || [];
      crm
        .subscribe({
          email: email,
          source: SOURCE,
          listId: config.KLAVIYO_LIST_ID,
          properties: {
            quiz_segment: state.segment,
            quiz_profile: state.answers.q1 || '',
            quiz_intent: state.answers.q16 || '',
            quiz_tried: tried.join(', '),
          },
        })
        .then(function (result) {
          if (result && result.ok) {
            crm.event(EVENT_NAME, { segment: state.segment, funnel: 'quiz-v3' }, { email: email });
            through();
            return;
          }
          if (result && result.reason === 'invalid_email') {
            fail('That email address was refused. Check it and try again.');
            track('Quiz Gate Failed', { reason: 'invalid_email' });
            return;
          }
          // The report is not held hostage to Klaviyo's availability.
          track('Quiz Gate Failed', { reason: (result && result.reason) || 'unknown' });
          through();
        })
        .catch(function () {
          track('Quiz Gate Failed', { reason: 'exception' });
          through();
        });
    }

    /* ---- wiring ---- */

    root.addEventListener('click', function (event) {
      if (!(event.target instanceof window.Element)) return;

      var answer = event.target.closest('[data-dp-quiz-answer]');
      if (answer) {
        if (current === ENTRY && !state.started) {
          state.started = true;
          publish('quiz_start', {});
        }
        state.answers[current] = answer.getAttribute('data-dp-value');
        save();
        advance();
        return;
      }

      var multi = event.target.closest('[data-dp-quiz-multi]');
      if (multi) {
        var value = multi.getAttribute('data-dp-value');
        var pressed = multi.getAttribute('aria-pressed') === 'true';
        var box = multi.closest('.dp-quiz__answers');
        // "None of the above" with nothing else ticked is the whole answer: it moves
        // straight on, like a single-choice tap. With other options ticked it only
        // clears them — an instant advance there would discard choices the visitor can
        // still see, on a control they may have hit to undo.
        if (value === 'none') {
          var others = box.querySelectorAll(
            '[data-dp-quiz-multi][aria-pressed="true"]:not([data-dp-value="none"])'
          ).length;
          Array.prototype.forEach.call(box.querySelectorAll('[data-dp-quiz-multi]'), function (node) {
            node.setAttribute('aria-pressed', 'false');
          });
          multi.setAttribute('aria-pressed', 'true');
          if (others === 0) {
            state.answers.q9 = ['none'];
            save();
            advance();
          }
          return;
        }
        var none = box.querySelector('[data-dp-value="none"]');
        if (none) none.setAttribute('aria-pressed', 'false');
        multi.setAttribute('aria-pressed', pressed ? 'false' : 'true');
        return;
      }

      if (event.target.closest('[data-dp-quiz-continue]')) {
        if (current === 'q9') {
          var picked = [];
          Array.prototype.forEach.call(
            stepRoot('q9').querySelectorAll('[data-dp-quiz-multi][aria-pressed="true"]'),
            function (node) {
              picked.push(node.getAttribute('data-dp-value'));
            }
          );
          state.answers.q9 = picked.length ? picked : ['none'];
          save();
        }
        advance();
        return;
      }

      if (event.target.closest('[data-dp-quiz-skip]')) {
        state.segment = computeSegment(state.answers);
        state.gated = true;
        save();
        track('Quiz Gate Skipped', {});
        publish('quiz_email_skip', {});
        show('loader');
        return;
      }

      if (event.target.closest('[data-dp-quiz-back]')) {
        var back = previousStep(current, state.answers);
        if (back) show(back);
        return;
      }

      if (event.target.closest('[data-dp-quiz-to-picker]')) {
        publish('quiz_cta_click', { cta: 'start_now' });
        // Past the picker section's gallery and chips, straight to the offer heading.
        var picker = document.querySelector('.dp-qp__heading') || document.querySelector('[data-dp-qp]');
        if (picker) picker.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth' });
      }
    });

    var gateForm = root.querySelector('[data-dp-quiz-gate]');
    if (gateForm) {
      gateForm.addEventListener('submit', function (event) {
        event.preventDefault();
        submitGate(gateForm);
      });
    }

    // The step id in the hash, with anything appended after it cut off: a pasted
    // "#plan?currency=GBP" (a query AFTER the hash, which the server never sees)
    // still reads as the plan instead of an id that matches nothing.
    function hashStep() {
      return (window.location.hash || '').slice(1).split(/[?&]/)[0];
    }

    window.addEventListener('popstate', function (event) {
      var id = (event.state && event.state.dpQuiz) || hashStep();
      if (id && allowedTarget(id, state)) {
        show(id, { silent: true });
      } else {
        show(ENTRY, { silent: true });
      }
    });

    /* ---- start ---- */

    /* The entry screen's copy: the default set ships visible; `?intro=original` swaps
       in the original set (client, 2026-10-18), so an ad can choose its landing line.
       Liquid cannot read a query string, so both sets are rendered and this picks. */
    try {
      var introKey = new URLSearchParams(window.location.search).get('intro');
      if (introKey) {
        var introNodes = root.querySelectorAll('[data-dp-quiz-intro]');
        var introMatch = Array.prototype.some.call(introNodes, function (node) {
          return node.getAttribute('data-dp-quiz-intro') === introKey;
        });
        if (introMatch) {
          Array.prototype.forEach.call(introNodes, function (node) {
            node.hidden = node.getAttribute('data-dp-quiz-intro') !== introKey;
          });
        }
      }
    } catch (error) {
      // An old browser without URLSearchParams keeps the default copy.
    }

    load();

    // Restore multi-select pressed states so going back to q9 shows what was picked.
    (state.answers.q9 || []).forEach(function (value) {
      var node = root.querySelector('[data-dp-quiz-multi][data-dp-value="' + value + '"]');
      if (node) node.setAttribute('aria-pressed', 'true');
    });

    var hash = hashStep();
    var entry = ENTRY;
    if (hash && allowedTarget(hash, state)) {
      entry = hash;
    } else if (state.reached !== ENTRY && allowedTarget(state.reached, state)) {
      entry = state.reached;
    }

    if (entry === ENTRY && !state.answers.q1) track('Quiz Started', {});
    show(entry, { silent: true });
    try {
      window.history.replaceState({ dpQuiz: entry }, '', '#' + entry);
    } catch (error) {
      // As above: cosmetic.
    }
  }

  window.dpQuiz = {
    core: {
      STRETCH: STRETCH,
      FLAG_RULES: FLAG_RULES,
      stepsFor: stepsFor,
      answered: answered,
      eligible: eligible,
      flowFor: flowFor,
      nextStep: nextStep,
      previousStep: previousStep,
      computeSegment: computeSegment,
      allowedTarget: allowedTarget,
      progressPercent: progressPercent,
      stepCount: stepCount,
      isIntro: isIntro,
      isPostGate: isPostGate,
    },
  };

  if (document.readyState === 'complete') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
    window.addEventListener('load', init);
  }
})();
